import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Building2, Waves, Bed, Zap, Car, Sparkles } from 'lucide-react';
import './Home.css';

export const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const highlights = [
    {
      icon: <Building2 size={32} />,
      title: 'Grand Lawn & Banquet Halls',
      description: 'Beautifully manicured spaces with elegant architectural lighting',
      delay: '0s'
    },
    {
      icon: <Waves size={32} />,
      title: 'Swimming Pool Zone',
      description: 'Perfect for haldi, mehndi, and cocktail evenings',
      delay: '0.1s'
    },
    {
      icon: <Bed size={32} />,
      title: '12 AC Rooms',
      description: '8 Premium + 4 Deluxe rooms with modern furnishings',
      delay: '0.2s'
    },
    {
      icon: <Zap size={32} />,
      title: 'Signature Lighting',
      description: 'Fountains & elegant décor with power backup',
      delay: '0.3s'
    },
    {
      icon: <Car size={32} />,
      title: 'Valet & Security',
      description: 'Professional valet parking and trained bouncers',
      delay: '0.4s'
    },
    {
      icon: <Users size={32} />,
      title: 'Event Coordination',
      description: 'Expert team for timeline and logistics planning',
      delay: '0.5s'
    }
  ];

  const events = [
    { name: 'Weddings & Receptions', image: 'https://customer-assets.emergentagent.com/job_luxweddings/artifacts/4sn3e26g_image.png' },
    { name: 'Haldi Ceremonies', image: 'https://customer-assets.emergentagent.com/job_luxweddings/artifacts/1y6plkef_image.png' },
    { name: 'Poolside Events', image: 'https://customer-assets.emergentagent.com/job_luxweddings/artifacts/m6gqy56x_image.png' },
    { name: 'Mehndi Celebrations', image: 'https://customer-assets.emergentagent.com/job_luxweddings/artifacts/l37pzpku_image.png' }
  ];

  return (
    <div className="home-page">
      {/* Hero Section with Parallax */}
      <section className="hero-section" ref={heroRef}>
        <div className="hero-background" style={{ transform: `translateY(${scrollY * 0.5}px)` }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_luxweddings/artifacts/2gjfz72w_image.png" 
            alt="The Green Valley"
            className="hero-image"
          />
          <div className="hero-overlay"></div>
          <div className="hero-particles"></div>
        </div>
        <div className="hero-content">
          <div className="container">
            <div className="glass-card hero-card animate-fade-up">
              <div className="shimmer-text">
                <h1 className="hero-xl">Where Every Celebration<br />Finds Its Perfect Setting</h1>
              </div>
              <p className="body-xl mt-24">
                The crown jewel of Shastripuram – West Agra's most cherished wedding and celebration venue
              </p>
              <div className="hero-cta">
                <a href="https://wa.me/918826372788?text=Hi!%20I'd%20like%20to%20enquire%20about%20dates%20and%20packages." 
                   target="_blank" 
                   rel="noopener noreferrer" 
                   className="btn-primary">
                  <Sparkles size={20} style={{ marginRight: '8px' }} />
                  Enquire on WhatsApp
                </a>
                <Link to="/contact" className="btn-secondary">
                  Book a Visit
                  <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="section-padding highlights-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle text-gold">Our Excellence</span>
            <h2 className="heading-1 text-gradient">Signature Features</h2>
          </div>
          <div className="highlights-grid">
            {highlights.map((highlight, index) => (
              <div 
                key={index} 
                className="highlight-card glass-card"
                style={{ animationDelay: highlight.delay }}
              >
                <div className="highlight-icon">{highlight.icon}</div>
                <h3 className="heading-3">{highlight.title}</h3>
                <p className="body-regular">{highlight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spaces Preview with Depth */}
      <section className="section-padding spaces-preview parallax-section">
        <div className="container">
          <div className="two-column-layout">
            <div className="content-column">
              <span className="section-subtitle text-gold">Discover</span>
              <h2 className="heading-1 mb-24">Exquisite Spaces</h2>
              <p className="body-xl mb-32">
                From our grand lawn surrounded by fountains to elegant banquet halls and a stunning poolside venue, 
                each space is meticulously designed to create unforgettable moments.
              </p>
              <ul className="feature-list">
                <li>Grand Lawn with architectural lighting</li>
                <li>Air-conditioned Banquet Halls</li>
                <li>Sparkling Poolside Venue</li>
                <li>12 Luxury Guest Rooms</li>
              </ul>
              <Link to="/spaces" className="btn-primary mt-32">
                Explore Spaces
                <ArrowRight size={20} style={{ marginLeft: '8px' }} />
              </Link>
            </div>
            <div className="image-column">
              <div className="depth-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_luxweddings/artifacts/m6gqy56x_image.png" 
                  alt="Green Valley Poolside"
                  className="feature-image"
                />
                <div className="image-glow"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Carousel */}
      <section className="section-padding-small events-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle text-gold">Celebrations</span>
            <h2 className="heading-1 text-gradient">Events We Host</h2>
          </div>
          <div className="events-grid">
            {events.map((event, index) => (
              <div key={index} className="event-card">
                <div className="event-image-container">
                  <img src={event.image} alt={event.name} className="event-image" />
                  <div className="event-overlay">
                    <h3 className="heading-3">{event.name}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-48">
            <Link to="/events" className="btn-primary">
              View All Events
              <ArrowRight size={20} style={{ marginLeft: '8px' }} />
            </Link>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="section-padding location-section">
        <div className="container">
          <div className="two-column-layout">
            <div className="image-column">
              <div className="depth-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_luxweddings/artifacts/it86ckwb_IMG-20251012-WA0014.jpg" 
                  alt="Green Valley at Night"
                  className="feature-image"
                />
                <div className="image-glow"></div>
              </div>
            </div>
            <div className="content-column">
              <span className="section-subtitle text-gold">Visit Us</span>
              <h2 className="heading-1 mb-24">Shastripuram, West Agra</h2>
              <p className="body-xl mb-32">
                Conveniently located in the heart of West Agra, The Green Valley offers easy access from major routes 
                while providing a serene escape from the city bustle.
              </p>
              <div className="location-details">
                <p className="body-regular mb-16">
                  <strong className="text-gold">Address:</strong><br />
                  100 Ft Road, Near Krishna Indraprastha Colony<br />
                  Shastripuram, Agra – 282007
                </p>
                <p className="body-regular mb-32">
                  <strong className="text-gold">Phone:</strong> <a href="tel:+918826372788" className="text-gold">+91 8826372788</a><br />
                  <strong className="text-gold">Email:</strong> <a href="mailto:sales@greenvalleyagra.com" className="text-gold">sales@greenvalleyagra.com</a>
                </p>
              </div>
              <a href="https://www.google.com/maps/search/?api=1&query=The+Green+Valley+Shastripuram+Agra" target="_blank" rel="noopener noreferrer" className="btn-primary">
                Get Directions
                <ArrowRight size={20} style={{ marginLeft: '8px' }} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="glass-card cta-card">
            <h2 className="hero-medium text-center">Ready to Create Magical Moments?</h2>
            <p className="body-xl text-center mt-24 mb-32">
              Book your visit today and let us transform your special day into a lifelong memory
            </p>
            <div className="cta-buttons">
              <a href="https://wa.me/918826372788?text=Hi!%20I'd%20like%20to%20book%20a%20visit." 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 className="btn-primary">
                <Sparkles size={20} style={{ marginRight: '8px' }} />
                WhatsApp Us
              </a>
              <Link to="/contact" className="btn-secondary">
                Contact Form
                <ArrowRight size={20} style={{ marginLeft: '8px' }} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
